import { Range, getTrackBackground } from 'react-range';
import React, { useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IAppliance } from '../../interfaces';
import { IFilterDefaults } from '../../templates/ApplianceArchives';
import { faInfo } from '@fortawesome/free-solid-svg-icons';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

export default function ApplianceFilter(props: {
    filteredProducts: IAppliance[];
    allProducts: IAppliance[];
    setFilteredProducts: React.Dispatch<IAppliance[]>;
    filterDefaults: IFilterDefaults;
    path: string;
    filterSurfiState: number[];
    filterPowerState: number[];
    filterPriceState: string;
    filterInstallState: string;
    filterSizeState: string;
    filterMadeInState: string;
    powerArg: string;
    surfiArg: string;
    priceArg: string;
    sizeArg: string;
    madeInArg: string;
    installArg: string;
    applianceCategory: string;
    surfiLink: any;
    setPage: React.Dispatch<React.SetStateAction<number>>;
}) {
    const {
        setPage,
        filteredProducts,
        setFilteredProducts,
        allProducts,
        filterDefaults,
        filterPowerState,
        filterSurfiState,
        filterPriceState,
        filterInstallState,
        filterSizeState,
        filterMadeInState,
        path,
        powerArg,
        surfiArg,
        priceArg,
        installArg,
        sizeArg,
        applianceCategory,
        surfiLink,
        madeInArg,
    } = props;

    const { currentLocale, navigateTranslated } = usePageLocalization();
    const STEPS = 5;
    const { t } = useTranslation('layout');
    const priceCategory = getPriceArray(t, applianceCategory);

    let sizeCategory;
    if (applianceCategory.includes('/Wood Stoves/')) {
        sizeCategory = [
            { value: 'sm', label: t('appliance.size.s') },
            { value: 'md', label: t('appliance.size.m') },
            { value: 'lg', label: t('appliance.size.l') },
            { value: 'xl', label: t('appliance.size.xl') },
        ];
    }

    const madeInChoices = [
        { value: 'canada', label: t('appliance.canada') },
        { value: 'usa', label: t('appliance.usa') },
    ];

    const certified = [
        {
            label: { fr: 'Maison mobile', en: 'Mobile home' },
            value: 'maisonmobile',
        },
        { label: { fr: 'Alcôve', en: 'Alcove' }, value: 'alcove' },
        { label: { fr: 'EPA 2020', en: 'EPA 2020' }, value: 'epa2020' },
    ];

    let isCategoryAllowedCertified = true;
    let isCategoryAllowedSurfi = true;
    let isCategoryAllowedPower = true;
    let isCategoryAllowedEpa = false;
    const outdoorFireplace = applianceCategory.includes('/Outdoor Fireplaces/');
    const campStove = applianceCategory.includes('/Camp Stove/');
    if (
        applianceCategory.includes('/Cookstoves/') ||
        applianceCategory.includes('/Gas Wall Mounted Room Heater/') ||
        outdoorFireplace ||
        campStove
    ) {
        isCategoryAllowedSurfi = false;
    }
    if (
        applianceCategory.includes('/Cookstoves/') ||
        applianceCategory.includes('/Gas Wall Mounted Room Heater/') ||
        outdoorFireplace ||
        applianceCategory.includes('/Wood Furnaces/') ||
        campStove
    ) {
        isCategoryAllowedPower = false;
    }

    if (
        applianceCategory.includes('/Gas Wall Mounted Room Heater/') ||
        applianceCategory.includes('/Wood Inserts/') ||
        applianceCategory.includes('/Wood Furnaces/') ||
        campStove
    )
        isCategoryAllowedCertified = false;

    if (
        applianceCategory.includes('/Wood Stoves/') ||
        applianceCategory.includes('/Wood Inserts/') ||
        applianceCategory.includes('/Wood Furnaces/') ||
        applianceCategory.includes('/Pellet Stoves/')
    )
        isCategoryAllowedEpa = true;

    const prodWithPrices: IAppliance[] = [];
    filteredProducts.concat(allProducts).forEach((prod) => {
        if (!prodWithPrices.find((e) => e.code === prod.code)) {
            prodWithPrices.push(prod);
        }
    });

    useEffect(() => {
        let prod: IAppliance[] = prodWithPrices;
        if (isCategoryAllowedSurfi) {
            prod = prodWithPrices?.filter((e: any) =>
                filterSurfi(e, filterSurfiState, currentLocale, filterDefaults)
            );
        }
        if (isCategoryAllowedPower) {
            prod = prod.filter((e) =>
                filterPower(e, filterPowerState, currentLocale, filterDefaults)
            );
        }
        if (filterInstallState.includes('maisonmobile')) {
            prod = prod.filter((e) => filterAttributes(e, 'MaiMob'));
        } else if (filterInstallState.includes('alcove')) {
            prod = prod.filter((e) => filterAttributes(e, 'Alcove'));
        } else if (filterInstallState.includes('epa2020')) {
            prod = prod.filter((e) =>
                filterAttributes(e, 'certifications', 'epa')
            );
        }
        
        prod = prod.filter((e) => filterBySize(e, filterSizeState));
        prod = prod.filter((e) => filterByMadeIn(e, filterMadeInState));
        prod = prod.filter((e) => filterByPrice(e, filterPriceState));
        setFilteredProducts(prod);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentLocale,
        filterInstallState,
        filterPowerState,
        filterPriceState,
        filterSurfiState,
        filterSizeState,
        setFilteredProducts,
    ]);

    const SingleFilter = (props: {
        filterName: string;
        filterType: 'AREA' | 'POWER';
        unit: string;
        className: string;
        relatedLink?: any;
    }) => {
        const {
            filterName,
            filterType,
            unit,
            className = '',
            relatedLink = false,
        } = props;
        const singleFilterData = getSingleFilterData(
            filterDefaults,
            filterSurfiState,
            filterPowerState,
            filterType
        );
        const [currentValues, setCurrentValues] = useState(
            singleFilterData.state
        );
        return (
            <div
                className={`single-filter heater-filter-category ${className}`}
            >
                <h4 className='heater-section-title'>{filterName}</h4>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        paddingRight: '1.5rem',
                    }}
                >
                    <Range
                        values={currentValues}
                        step={STEPS}
                        min={singleFilterData.min}
                        max={singleFilterData.max}
                        onChange={(values) => {
                            setCurrentValues(values);
                        }}
                        renderTrack={({ props, children }) => (
                            <div
                                className='track'
                                onMouseDown={props.onMouseDown}
                                onTouchStart={props.onTouchStart}
                            >
                                <div
                                    className='track-bg'
                                    ref={props.ref}
                                    style={{
                                        background: getTrackBackground({
                                            values: currentValues,
                                            colors: [
                                                '#0F0F0F',
                                                '#D65E2A',
                                                '#0F0F0F',
                                            ],
                                            min: singleFilterData.min,
                                            max: singleFilterData.max,
                                        }),
                                    }}
                                >
                                    {children}
                                </div>
                            </div>
                        )}
                        renderThumb={({ props, isDragged }) => (
                            <div {...props} className='thumb'>
                                <div className='outter-thumb' />
                            </div>
                        )}
                        onFinalChange={(values) => {
                            const scrollPosition =
                                document.documentElement.scrollTop ||
                                document.body.scrollTop;
                            switch (filterType) {
                                case 'AREA':
                                    navigateTranslatedWoodHeater(
                                        sizeArg,
                                        madeInArg,
                                        powerArg,
                                        `${values[0]}-${values[1]}`,
                                        installArg,
                                        priceArg,
                                        scrollPosition
                                    );

                                    break;
                                case 'POWER':
                                    navigateTranslatedWoodHeater(
                                        sizeArg,
                                        madeInArg,
                                        `${values[0]}-${values[1]}`,
                                        surfiArg,
                                        installArg,
                                        priceArg,
                                        scrollPosition
                                    );                                  
                                    break;
                            }
                        }}
                    />

                    <div className='values'>
                        <span className='value leftValue'>{`${currentValues[0]}${unit}`}</span>
                        <span className='value rightValue'>{`${currentValues[1]}${unit}`}</span>
                    </div>
                </div>
                {relatedLink && (
                    <div className='filter-related-link'>
                        <div className='icon-container'>
                            <span className='icon-border'>
                                <FontAwesomeIcon
                                    className='icon'
                                    icon={faInfo}
                                />
                            </span>
                        </div>
                        <span
                            className='link-title'
                            onClick={() =>
                                navigateTranslated(
                                    surfiLink?.link?.values[0][currentLocale]
                                )
                            }
                        >
                            {surfiLink?.texte_1?.values[0][currentLocale]}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    const navigateTranslatedWoodHeater = (
        sizeArg: any,
        madeInArg: any,
        powerArg: any,
        surfiArg: any,
        installArg: any,
        priceArg: any,
        scrollPosition: any
    ) => {
        navigateTranslated(
            `${path}${sizeArg ? `&size=${sizeArg}` : ''}${
                madeInArg ? `&madeIn=${madeInArg}` : ''
            }${powerArg ? `&power=${powerArg}` : ''}${
                surfiArg ? `&surfi=${surfiArg}` : ''
            }${installArg ? `&install=${installArg}` : ''}${
                priceArg ? `&price=${priceArg}` : ''
            }`,
            scrollPosition
        );
    };

    return (
        <div
            className={
                (sizeCategory ||
                    isCategoryAllowedCertified ||
                    isCategoryAllowedSurfi ||
                    isCategoryAllowedPower ||
                    isCategoryAllowedEpa) &&
                !outdoorFireplace
                    ? 'archive-page-filter'
                    : 'archive-page-filter no-filter'
            }
        >
            {(sizeCategory ||
                madeInChoices ||
                isCategoryAllowedCertified ||
                isCategoryAllowedSurfi ||
                isCategoryAllowedPower ||
                isCategoryAllowedEpa) &&
                !outdoorFireplace && (
                    <>
                        <h4 className='heater-filter-title'>
                            {t('venting.filter')}
                        </h4>

                        {madeInChoices && (
                            <div className='heater-filter-category size'>
                                <h3 className='heater-section-title'>
                                    {t('appliance.filter.made-in')}
                                </h3>
                                <div className='filter-checkbox-group'>
                                    {madeInChoices?.map((single, index) => {
                                        return (
                                            <SingleCheckBox
                                                key={`madeInCategory-key-${index}`}
                                                preId={'heater-made-in-single'}
                                                index={index}
                                                single={single}
                                                checkFlag={sizeArg?.includes(
                                                    single.value
                                                )}
                                                onChange={(checked) => {
                                                    const scrollPosition =
                                                        document.documentElement
                                                            .scrollTop ||
                                                        document.body.scrollTop;

                                                    if (checked) {
                                                        const tmpMadeInArg =
                                                            madeInArg != ''
                                                                ? `${madeInArg},${single.value}`
                                                                : `${single.value}`;

                                                        navigateTranslatedWoodHeater(
                                                            sizeArg,
                                                            tmpMadeInArg,
                                                            powerArg,
                                                            surfiArg,
                                                            installArg,
                                                            priceArg,
                                                            scrollPosition
                                                        );
                                                    } else {
                                                        const madeInArgTmp: any =
                                                            madeInArg
                                                                ?.split(',')
                                                                .filter(
                                                                    (e) =>
                                                                        e !==
                                                                        single.value
                                                                )
                                                                .concat(',')
                                                                .filter(
                                                                    (e) =>
                                                                        e !==
                                                                            '' &&
                                                                        e !==
                                                                            ','
                                                                ) || '';
                                                        console.log(
                                                            'tmpMadeInArg============='
                                                        );
                                                        console.log(
                                                            madeInArgTmp
                                                        );
                                                        navigateTranslatedWoodHeater(
                                                            sizeArg,
                                                            madeInArgTmp.length !==
                                                                0
                                                                ? `${madeInArgTmp}&`
                                                                : ``,
                                                            powerArg,
                                                            surfiArg,
                                                            installArg,
                                                            priceArg,
                                                            scrollPosition
                                                        );
                                                    }
                                                }}
                                            ></SingleCheckBox>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {sizeCategory && (
                            <div className='heater-filter-category size'>
                                <h3 className='heater-section-title'>
                                    {t('appliance.filter.size')}
                                </h3>
                                <div className='filter-checkbox-group'>
                                    {sizeCategory?.map((single, index) => {
                                        return (
                                            <SingleCheckBox
                                                key={`sizeCategory-key-${index}`}
                                                preId={'heater-size-single'}
                                                index={index}
                                                single={single}
                                                checkFlag={sizeArg?.includes(
                                                    single.value
                                                )}
                                                onChange={(checked) => {
                                                    const scrollPosition =
                                                        document.documentElement
                                                            .scrollTop ||
                                                        document.body.scrollTop;

                                                    if (checked) {
                                                        const tmpSize =
                                                            sizeArg != ''
                                                                ? `${sizeArg},${single.value}`
                                                                : `${single.value}`;

                                                        navigateTranslatedWoodHeater(
                                                            tmpSize,
                                                            madeInArg,
                                                            powerArg,
                                                            surfiArg,
                                                            installArg,
                                                            priceArg,
                                                            scrollPosition
                                                        );                                                        
                                                    } else {
                                                        const sizeArgTmp: any =
                                                            sizeArg
                                                                ?.split(',')
                                                                .filter(
                                                                    (e) =>
                                                                        e !==
                                                                        single.value
                                                                )
                                                                .concat(',')
                                                                .filter(
                                                                    (e) =>
                                                                        e !==
                                                                            '' &&
                                                                        e !==
                                                                            ','
                                                                ) || '';
                                                        
                                                        navigateTranslatedWoodHeater(
                                                            sizeArgTmp.length !==
                                                                0
                                                                ? sizeArgTmp
                                                                : '',
                                                            madeInArg,
                                                            powerArg,
                                                            surfiArg,
                                                            installArg,
                                                            priceArg,
                                                            scrollPosition
                                                        );                                                       
                                                    }
                                                }}
                                            ></SingleCheckBox>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {(isCategoryAllowedCertified || isCategoryAllowedEpa) &&
                            !outdoorFireplace && (
                                <div className='heater-filter-category certified'>
                                    <h3 className='heater-section-title'>
                                        {t('appliance.certified-installation')}
                                    </h3>
                                    <div className='filter-checkbox-group'>
                                        {certified.map((single, index) => {
                                            if (
                                                isCategoryAllowedCertified ||
                                                (!isCategoryAllowedCertified &&
                                                    single.value === 'epa2020')
                                            )
                                                return (
                                                    <SingleCheckBox
                                                        key={`certified-install-key-${index}`}
                                                        preId={
                                                            'heater-certified-installer-single'
                                                        }
                                                        index={index}
                                                        single={single}
                                                        checkFlag={installArg?.includes(
                                                            single.value
                                                        )}
                                                        onChange={(checked) => {
                                                            const scrollPosition =
                                                                document
                                                                    .documentElement
                                                                    .scrollTop ||
                                                                document.body
                                                                    .scrollTop;
                                                            if (checked) {
                                                                const tempInstallArg =
                                                                    installArg !=
                                                                    ''
                                                                        ? `${installArg},${single.value}`
                                                                        : `${single.value}`;
                                                                navigateTranslatedWoodHeater(
                                                                    sizeArg,
                                                                    madeInArg,
                                                                    powerArg,
                                                                    surfiArg,
                                                                    tempInstallArg,
                                                                    priceArg,
                                                                    scrollPosition
                                                                );
                                                            } else {
                                                                const installArgTmp: any =
                                                                    installArg
                                                                        ?.split(
                                                                            ','
                                                                        )
                                                                        .filter(
                                                                            (
                                                                                e
                                                                            ) =>
                                                                                e !==
                                                                                single.value
                                                                        )
                                                                        .concat(
                                                                            ','
                                                                        )
                                                                        .filter(
                                                                            (
                                                                                e
                                                                            ) =>
                                                                                e !==
                                                                                    '' &&
                                                                                e !==
                                                                                    ','
                                                                        ) || '';

                                                                const tempInstallArg =
                                                                    installArgTmp.length !==
                                                                    0
                                                                        ? `${installArgTmp}&`
                                                                        : ``;

                                                                navigateTranslatedWoodHeater(
                                                                    sizeArg,
                                                                    madeInArg,
                                                                    powerArg,
                                                                    surfiArg,
                                                                    tempInstallArg,
                                                                    priceArg,
                                                                    scrollPosition
                                                                );                                                               
                                                            }
                                                        }}
                                                    />
                                                );
                                            else return undefined;
                                        })}
                                    </div>
                                </div>
                            )}

                        <div className='small-div'>
                            <div className='small-price'>
                                <h3 className='heater-section-title'>
                                    {t('venting.price')}
                                </h3>
                                <div className='filter-checkbox-group'>
                                    {priceCategory.map((single, index) => {
                                        return (
                                            <SingleCheckBox
                                                key={`price-category-small-key-${index}`}
                                                preId={'heater-price-single'}
                                                index={index}
                                                single={single}
                                                checkFlag={priceArg?.includes(
                                                    single.value
                                                )}
                                                onChange={(checked) => {
                                                    const scrollPosition =
                                                        document.documentElement
                                                            .scrollTop ||
                                                        document.body.scrollTop;

                                                    if (checked) {
                                                        navigateTranslated(
                                                            `${path}price=${
                                                                priceArg
                                                                    ? `${priceArg},${single.value}`
                                                                    : `${single.value}`
                                                            }${
                                                                powerArg
                                                                    ? `&power=${powerArg}`
                                                                    : ''
                                                            }${
                                                                surfiArg
                                                                    ? `&surfi=${surfiArg}`
                                                                    : ''
                                                            }${
                                                                installArg
                                                                    ? `&install=${installArg}`
                                                                    : ''
                                                            }${
                                                                sizeArg
                                                                    ? `&size=${sizeArg}`
                                                                    : ``
                                                            }`,
                                                            scrollPosition
                                                        );
                                                    } else {
                                                        const priceArgTmp: any =
                                                            priceArg
                                                                ?.split(',')
                                                                .filter(
                                                                    (e) =>
                                                                        e !==
                                                                        single.value
                                                                )
                                                                .concat(',')
                                                                .filter(
                                                                    (e) =>
                                                                        e !==
                                                                            '' &&
                                                                        e !==
                                                                            ','
                                                                ) || '';
                                                        navigateTranslated(
                                                            `${path}${
                                                                priceArgTmp.length !==
                                                                0
                                                                    ? `price=${priceArgTmp}&`
                                                                    : ``
                                                            }${
                                                                powerArg
                                                                    ? `power=${powerArg}&`
                                                                    : ''
                                                            }${
                                                                surfiArg
                                                                    ? `surfi=${surfiArg}&`
                                                                    : ''
                                                            }${
                                                                installArg
                                                                    ? `install=${installArg}`
                                                                    : ''
                                                            }${
                                                                sizeArg
                                                                    ? `&size=${sizeArg}`
                                                                    : ``
                                                            }`,
                                                            scrollPosition
                                                        );
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                            <div className='small-certified'>
                                <h3 className='heater-section-title'>
                                    {t('appliance.certified-installation')}
                                </h3>
                                <div className='filter-checkbox-group'>
                                    {certified.map((single, index) => {
                                        return (
                                            <SingleCheckBox
                                                key={`certified-small-key-${index}`}
                                                preId={
                                                    'heater-certified-installer-single'
                                                }
                                                index={index}
                                                single={single}
                                                checkFlag={installArg?.includes(
                                                    single.value
                                                )}
                                                onChange={(checked) => {
                                                    const scrollPosition =
                                                        document.documentElement
                                                            .scrollTop ||
                                                        document.body.scrollTop;
                                                    if (checked) {
                                                        navigateTranslated(
                                                            `${path}install=${
                                                                installArg
                                                                    ? `${installArg},${single.value}`
                                                                    : `${single.value}`
                                                            }${
                                                                priceArg
                                                                    ? `&price=${priceArg}`
                                                                    : ``
                                                            }${
                                                                powerArg
                                                                    ? `&power=${powerArg}`
                                                                    : ''
                                                            }${
                                                                surfiArg
                                                                    ? `&surfi=${surfiArg}`
                                                                    : ''
                                                            }${
                                                                sizeArg
                                                                    ? `&size=${sizeArg}`
                                                                    : ``
                                                            }`,
                                                            scrollPosition
                                                        );
                                                    } else {
                                                        const installArgTmp: any =
                                                            installArg
                                                                ?.split(',')
                                                                .filter(
                                                                    (e) =>
                                                                        e !==
                                                                        single.value
                                                                )
                                                                .concat(',')
                                                                .filter(
                                                                    (e) =>
                                                                        e !==
                                                                            '' &&
                                                                        e !==
                                                                            ','
                                                                ) || '';
                                                        navigateTranslated(
                                                            `${path}${
                                                                installArgTmp.length !==
                                                                0
                                                                    ? `install=${installArgTmp}&`
                                                                    : ``
                                                            }${
                                                                powerArg
                                                                    ? `power=${powerArg}&`
                                                                    : ''
                                                            }${
                                                                surfiArg
                                                                    ? `surfi=${surfiArg}&`
                                                                    : ''
                                                            }${
                                                                priceArg
                                                                    ? `&price=${priceArg}`
                                                                    : ``
                                                            }${
                                                                sizeArg
                                                                    ? `&size=${sizeArg}`
                                                                    : ``
                                                            }`,
                                                            scrollPosition
                                                        );
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {isCategoryAllowedSurfi && (
                            <SingleFilter
                                filterName={t('appliance.square-foot')}
                                filterType='AREA'
                                unit={
                                    currentLocale === 'fr_CA' ? ' pi²' : ' ft²'
                                }
                                className='single-filter'
                                relatedLink={true}
                            />
                        )}
                        {isCategoryAllowedPower && (
                            <SingleFilter
                                filterName={t('appliance.power')}
                                filterType='POWER'
                                unit=' BTU/h'
                                className='single-filter'
                            />
                        )}

                        <div className='heater-filter-category reset'>
                            <span
                                className='heater-reset-filter'
                                onClick={() => {
                                    const scrollPosition =
                                        document.documentElement.scrollTop ||
                                        document.body.scrollTop;
                                    navigateTranslated(path, scrollPosition);
                                }}
                            >
                                {t('venting.reset-filter')}
                            </span>
                        </div>
                    </>
                )}
        </div>
    );
}
function getSingleFilterData(
    filterDefaults: IFilterDefaults,
    filterSurfiState: number[],
    filterPowerState: number[],
    filterType: 'AREA' | 'POWER'
) {
    const returnData = {
        min: filterDefaults.minVal,
    };
    switch (filterType) {
        case 'AREA':
            return {
                ...returnData,
                state: filterSurfiState,
                max: filterDefaults.maxArea,
            };

        case 'POWER':
            return {
                ...returnData,
                state: filterPowerState,
                max: filterDefaults.maxPower,
            };
    }
}

function SingleCheckBox(props: {
    index: number;
    single: any;
    onChange: (checked: boolean) => void;
    preId: string;
    checkFlag?: boolean;
}) {
    const { currentLocale } = usePageLocalization();
    const { index, single, onChange, preId, checkFlag } = props;
    const [isChecked, setChecked] = useState(false);
    useEffect(() => {
        if (!checkFlag) {
            setChecked(false);
        } else {
            setChecked(true);
        }
    }, [checkFlag]);
    const locale = currentLocale === 'fr_CA' ? 'fr' : 'en';
    return (
        <div className='checkbox-and-label' key={`key-${preId}${index}`}>
            <input
                id={`${preId}${index}`}
                type='checkbox'
                onChange={(event) => {
                    if (onChange) {
                        onChange(event.target.checked);
                        setChecked(event.target.checked);
                    }
                }}
                checked={isChecked}
            />
            <label htmlFor={`${preId}${index}`} className='checkbox-label'>
                {single.label?.[locale] ? single.label[locale] : single.label}
            </label>
        </div>
    );
}

function filterSurfi(
    product: {
        attributes: {
            Surfi: { values: any[] };
            available_country: { values: any[] };
        };
    },
    filterState: number[],
    currentLocale: string,
    filterDefaults: IFilterDefaults
): boolean {
    const productArea: string =
        product.attributes?.Surfi?.values[0]?.[currentLocale];
    let productMin = 0,
        productMax = 10000;
    if (productArea && productArea.includes('< ')) {
        productMin = parseInt(productArea.split('< ')[1].replace(',', ''));
        productMax = parseInt(productArea.split('< ')[1].replace(',', ''));
    } else if (productArea && productArea.includes(' - ')) {
        productMin = parseInt(productArea.split(' - ')[0].replace(',', ''));
        productMax = parseInt(productArea.split(' - ')[1].replace(',', ''));
    }
    const available_countries = product.attributes?.available_country?.values;

    const current_country =
        currentLocale.split('_')[1] === 'CA' ? 'Canada' : 'United States';

    const is_available_current_country =
        available_countries === undefined
            ? true
            : available_countries.some(
                  (country: any) => country[currentLocale] === current_country
              );

    const hasSurfiAttribute = product.attributes?.Surfi?.values !== undefined;

    if (
        !hasSurfiAttribute &&
        filterState[0] === filterDefaults.minVal &&
        filterState[1] === filterDefaults.maxArea
    ) {
        // Si le slider de superficie n'as pas été modifier,
        // on affiche dans le listing les appareils dont l'attribut est null
        return true;
    }

    return (
        is_available_current_country &&
        hasSurfiAttribute &&
        ((productMax <= filterState[1] && productMax >= filterState[0]) ||
            (productMin >= filterState[0] && productMin <= filterState[1]) ||
            !productArea)
    );
}
function filterPower(
    product: any,
    filterState: number[],
    currentLocale: string,
    filterDefaults: IFilterDefaults
): boolean {
    const attributeName = product.category.includes('/Pellet Stoves/')
        ? 'PWMaEni'
        : 'PWMaSoi';
    const productPower = parseInt(
        product.attributes?.[attributeName]?.values[0]?.[currentLocale]
            .split(' ')[0]
            .replace(',', '')
    );

    const available_countries = product.attributes?.available_country?.values;

    const current_country =
        currentLocale.split('_')[1] === 'CA' ? 'Canada' : 'United States';

    const is_available_current_country =
        available_countries === undefined
            ? true
            : available_countries.some(
                  (country: any) => country[currentLocale] === current_country
              );

    const hasPowerAttribute =
        product.attributes?.PWMaSoi ||
        (filterState[0] === filterDefaults.minVal &&
            filterState[1] === filterDefaults.maxPower)
            ? true
            : false;

    if (
        !hasPowerAttribute &&
        filterState[0] === filterDefaults.minVal &&
        filterState[1] === filterDefaults.maxPower
    ) {
        // Si le slider de Puissance n'as pas été modifier,
        // on affiche dans le listing les appareils dont l'attribut est null
        return true;
    }

    return (
        is_available_current_country &&
        hasPowerAttribute &&
        ((productPower >= filterState?.[0] &&
            productPower <= filterState?.[1]) ||
            isNaN(productPower))
    );
}

function filterByPrice(product: any, filterState: string): boolean {
    const valueArr = filterState.split(',');
    let max: any = null;
    let min: any = null;
    valueArr.forEach((e) => {
        try {
            const numberArr = e.split('-');
            numberArr.forEach((numb) => {
                if (!isNaN(parseInt(numb))) {
                    const number = parseInt(numb);
                    if (min === null || number <= min) {
                        min = number;
                    }
                    if (max === null || number >= max) {
                        max = number;
                    }
                }
            });
        } catch (e) {
            console.error(e);
        }
    });
    const regularPrice: number = product?.price?.regularPrice?.value
        ? product?.price?.regularPrice?.value
        : 0;
    if (
        min === null ||
        max === null ||
        (regularPrice <= max && regularPrice >= min)
    ) {
        return true;
    }
    return false;
}

function filterByMadeIn(product: any, filterState: string): boolean {
    const isMadeInCanada = product?.price?.madeIn?.isMadeInCanada ?? false;
    const isMadeInUSA = product.price.madeIn.isMadeInUSA ?? false;
    
    if (filterState == '') {
        return true;
    }
    return filterState?.split(',')?.some((e) => {
        switch (e) {
            case 'canada':
                return isMadeInCanada;
            case 'usa':
                return isMadeInUSA;
            default:
                return true;
        }
    });
}

function filterBySize(product: any, filterState: string): boolean {
    const size: string = product?.attributes?.size?.values?.[0]['en_CA'];

    return filterState?.split(',')?.some((e) => {
        switch (e) {
            case 'sm':
                return size === 'Small';
            case 'md':
                return size === 'Medium';
            case 'lg':
                return size === 'Large';
            case 'xl':
                return size === 'Extra Large';
            default:
                return true;
        }
    });
}

function filterAttributes(
    e: IAppliance,
    attribute: string,
    type = 'default'
): boolean {
    switch (type) {
        case 'epa': {
            if (e.attributes?.[attribute]?.values[0]['fr_CA'] === 'EPA-2020') {
                return true;
            } else {
                return false;
            }
        }

        default: {
            if (
                e.attributes?.[attribute]?.values[0]['fr_CA']
                    ?.toLowerCase()
                    ?.includes('oui')
            ) {
                return true;
            } else {
                return false;
            }
        }
    }
}
function getPriceArray(t: any, category: string) {
    const deff = [
        {
            label: t('appliance.price-range', { from: '0', to: '999' }),
            value: '0-999',
        },
        {
            label: t('appliance.price-range', { from: '1000', to: '1999' }),
            value: '1000-1999',
        },
        {
            label: t('appliance.price-and-more', { from: '2000' }),
            value: '2000-20000',
        },
    ];
    if (category) {
        if (category.includes('Wood Stoves')) {
            return [
                /*{
                    label: t('appliance.price-range', {
                        from: '0',
                        to: '999',
                    }),
                    value: '0-999',
                },*/
                {
                    label: t('appliance.price-range', {
                        from: '1000',
                        to: '1499',
                    }),
                    value: '1000-1499',
                },
                {
                    label: t('appliance.price-range', {
                        from: '1500',
                        to: '1999',
                    }),
                    value: '1500-1999',
                },
                {
                    label: t('appliance.price-and-more', { from: '2000' }),
                    value: '2000-20000',
                },
            ];
        } else if (category.includes('Pellet Stoves')) {
            return [
                {
                    label: t('appliance.price-range', {
                        from: '0',
                        to: '2999',
                    }),
                    value: '0-2999',
                },
                {
                    label: t('appliance.price-and-more', { from: '3000' }),
                    value: '3000-30000',
                },
            ];
        } else if (category.includes('Wood Inserts')) {
            return [
                {
                    label: t('appliance.price-range', {
                        from: '0',
                        to: '1999',
                    }),
                    value: '0-1999',
                },
            ];
        } else if (category.includes('Wood Furnaces')) {
            return [
                {
                    label: t('appliance.price-range', {
                        from: '0',
                        to: '3799',
                    }),
                    value: '0-3799',
                },
                {
                    label: t('appliance.price-and-more', { from: '3800' }),
                    value: '3800-38000',
                },
            ];
        } else if (category.includes('Cookstoves')) {
            return [
                {
                    label: t('appliance.price-range', {
                        from: '0',
                        to: '2999',
                    }),
                    value: '0-2999',
                },
                {
                    label: t('appliance.price-and-more', { from: '3000' }),
                    value: '3000-30000',
                },
            ];
        } else if (category.includes('Outdoor Fireplaces')) {
            return [
                {
                    label: t('appliance.price-range', {
                        from: '0',
                        to: '499',
                    }),
                    value: '0-499',
                },
                {
                    label: t('appliance.price-and-more', { from: '500' }),
                    value: '500-50000',
                },
            ];
        } else if (category.includes('Gas Wall Mounted Room Heater')) {
            return [
                {
                    label: t('appliance.price-range', {
                        from: '0',
                        to: '1999',
                    }),
                    value: '0-1999',
                },
            ];
        } else {
            return deff;
        }
    }

    return deff;
}
